import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ArrowUpOnSquareIcon } from '@heroicons/react/24/solid';
import { Button, Dialog, DialogContent, TextField, Tooltip } from '@mui/material';
import styled, { useTheme } from 'styled-components';

import SearchIconSrc from '@/assets/icons/search.svg';
import { TenantEntities } from '@/features/canvas/api/tenant-sso';
import { importViewers } from '@/features/publicLinks/api';
import StyledButton from '@/features/ui/components/Button/Button';
import Icon from '@/features/ui/components/Icon/Icon';
import { useShowToast } from '@/hooks/useShowToast';

import { Actions } from '../ViewerTable/components/EditViewer';

const Header = ({
  activeView,
  isLoading,
  setAddEntity,
  setEntities,
  searchText,
  setSearchText,
  maxViewersCount,
  viewersCount,
}: {
  activeView: 'viewers' | 'groups';
  isLoading: boolean;
  searchText: string;
  setAddEntity: Dispatch<SetStateAction<boolean>>;
  setEntities: Dispatch<SetStateAction<TenantEntities>>;
  setSearchText: Dispatch<SetStateAction<string>>;
  viewersCount: number;
  maxViewersCount?: number;
}) => {
  const { showToast } = useShowToast();
  const theme = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  const { mutateAsync } = useMutation((file: File) => importViewers(file), {
    onSuccess: (data: TenantEntities['viewers']) => {
      setEntities(prev => ({
        ...prev,
        viewers: [
          ...prev.viewers,
          ...data.filter(item => !prev.viewers.some(x => x.email === item.email)),
        ].sort((a, b) => {
          if (!a.name) return 1;
          if (!b.name) return -1;
          return a.name.localeCompare(b.name);
        }),
      }));
      showToast('success', t('access.import_dialog', { count: data.length }));
    },
    onError: () => setIsImportDialogOpen(true),
  });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      showToast('success', t('access.importing_viewers'));
      await mutateAsync(file).finally(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <HeaderWrapper>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <HeaderButton onClick={() => setAddEntity(true)}>
            <PlusIcon width={20} height={20} />
            {activeView === 'viewers' ? t('access.add_viewer') : t('access.add_group')}
          </HeaderButton>
          {activeView === 'viewers' && (
            <Actions>
              <Tooltip title={t('access.import_format')} placement="top">
                <HeaderButton onClick={handleImportClick}>
                  <ArrowUpOnSquareIcon width={20} height={20} />
                  {t('access.import_viewers')}
                </HeaderButton>
              </Tooltip>
            </Actions>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <SearchWrapper>
          {!isLoading && (
            <ViewersCountWrapper>
              {t('access.viewers_count', { viewersCount, maxViewersCount })}
            </ViewersCountWrapper>
          )}
          <StyledSearchTextField
            variant="standard"
            placeholder={
              activeView === 'viewers' ? t('access.search_viewer') : t('access.search_group')
            }
            value={searchText}
            InputProps={{
              disableUnderline: true,
              sx: {
                padding: '0 1rem',
              },
              onChange: e => setSearchText(e.currentTarget.value),
              startAdornment: (
                <StartAdornmentIconWrapper>
                  <Icon src={SearchIconSrc} />
                </StartAdornmentIconWrapper>
              ),
            }}
          />
        </SearchWrapper>
      </HeaderWrapper>
      <Dialog
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            color: theme.palette.brand.textPrimary,
            borderRadius: '3rem',
            minWidth: '500px',
          },
        }}
      >
        <StyledDialogContent>
          <ImportDialogTitle
            dangerouslySetInnerHTML={{ __html: t('access.import_error_dialog') }}
          />
          <StyledButton
            label={t('cancel')}
            variant="action"
            onClick={() => setIsImportDialogOpen(false)}
          />
        </StyledDialogContent>
      </Dialog>
    </>
  );
};

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  gap: 1rem;
`;

const ViewersCountWrapper = styled.p`
  text-align: end;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  font-weight: 600;
`;

const ImportDialogTitle = styled.h3`
  color: ${({ theme }) => theme.palette.brand.primary};
  font-size: 2rem;
  padding-bottom: 1rem;
  text-align: center;
`;

const StartAdornmentIconWrapper = styled.div`
  margin-right: 2rem;

  svg {
    display: flex;
    width: 2rem;
    height: 2rem;
  }
`;

const StyledSearchTextField = styled(TextField)`
  background-color: ${({ theme }) => theme.palette.brand.white};
  border-radius: 2rem;
  font-size: 2rem;
  border: 0;
  font-style: italic;
`;

const HeaderWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin-right: 20px;
`;

const HeaderButton = styled(Button)`
  color: ${({ theme }) => theme.palette.brand.primary};
  text-transform: none;
  font-weight: 600;
  font-size: 1.5rem;
  display: flex;
  gap: 0.75rem;
  border-radius: 20px;
`;

export default Header;
